import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import HomeFooter from "@/pages/home/components/HomeFooter";
import { guides } from "@/mocks/guides";

export default function GuidePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const guide = guides.find((g) => g.slug === slug);

  if (!guide) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 px-6 text-center">
          <h1 className="font-heading text-3xl font-semibold text-foreground-950 mb-4">Guide Not Found</h1>
          <p className="text-foreground-500 mb-6">The shopping guide you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
        <HomeFooter />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article>
        <header className="bg-background-100 pt-24 md:pt-28 pb-16 md:pb-20 px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs font-medium text-secondary-600 uppercase tracking-widest mb-4">
              /Shopping Guide
            </p>

            <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-8 border border-background-200/70">
              <img
                src={guide.imageUrl}
                alt={guide.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight mb-6">
              {guide.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-4">
              {guide.subtitle}
            </p>
            <div className="flex items-center gap-4 text-xs text-foreground-400">
              <span>Written by Editorial Team</span>
              <span>&middot;</span>
              <span>Reading Time: 6-8 min</span>
              <span>&middot;</span>
              <span>Updated June 2026</span>
            </div>
          </div>
        </header>

        <section className="py-16 md:py-20 px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="space-y-12">
              {guide.content.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                    {section.body}
                  </p>
                </section>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-6 md:px-10 bg-background-100">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-3">
              Explore More Shopping Guides
            </h2>
            <p className="text-base text-foreground-500 mb-8 leading-relaxed">
              Dive deeper into the pandabuyspreadsheet system with more in-depth guides covering every aspect of smarter shopping.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                PandaBuy Sheet
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-foreground-300 text-foreground-700 text-base font-medium hover:border-foreground-500 hover:text-foreground-950 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </article>

      <HomeFooter />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL ? import.meta.env.VITE_SITE_URL : 'https://pandabuy.boats') + '/'
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": guide.title,
                "item": (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL ? import.meta.env.VITE_SITE_URL : 'https://pandabuy.boats') + '/guides/' + guide.slug
              }
            ]
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": guide.title,
            "description": guide.subtitle,
            "author": {
              "@type": "Organization",
              "name": "PandaBuy Editorial Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "PandaBuy Boats",
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg"
              }
            },
            "datePublished": "2026-06-01",
            "dateModified": "2026-06-29",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_URL ? import.meta.env.VITE_SITE_URL : 'https://pandabuy.boats') + '/guides/' + guide.slug
            },
            "image": guide.imageUrl
          })
        }}
      />
    </main>
  );
}